import { useState, useRef, ChangeEvent, KeyboardEvent, useEffect } from "react";
import { useParams } from "react-router";
import { FaArrowUp, FaPlus, FaFile, FaCheck } from "react-icons/fa6";
import { FaTimes } from "react-icons/fa";
import { TbDatabase } from "react-icons/tb";
import DataSourceSelectorModal from "~/components/database/data-source-selector-modal";
import { useGlobalStore } from "~/stores/store-provider";
import { Connection } from "~/stores/slices/database";
import { useLoadingText } from "./useLoadingText";

interface MessageInputProps {
  onSendMessage: (message: string, attachments?: File[]) => void;
  disabled?: boolean;
  placeholder?: string;
  attachmentsRequired?: boolean;
  isLoading?: boolean;
  isMessageStreaming?: boolean;
  selectedDatabase?: string;
  onDatabaseSelect?: (database: string) => void;
  isDatabaseSelectionDisabled?: boolean;
  isHome?: boolean;
  selectedDataSources?: string[];
  onDataSourcesChange?: (ids: string[]) => void;
}

const MessageInput = ({
  onSendMessage,
  disabled = false,
  placeholder = "Ask Gamgee",
  attachmentsRequired = false,
  isLoading = false,
  isMessageStreaming = false,
  selectedDatabase = "demo_banking",
  onDatabaseSelect,
  isDatabaseSelectionDisabled = false,
  isHome = false,
  selectedDataSources = [],
  onDataSourcesChange,
}: MessageInputProps) => {
  const { sessionDocumentView, setSessionDocumentView } = useGlobalStore(
    (state: any) => state
  );
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isDataSourceModalOpen, setIsDataSourceModalOpen] = useState(false);
  const [shuffledPlaceholder, setShuffledPlaceholder] = useState("");
  const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(0);
  const [isShuffling, setIsShuffling] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [hasDocButtonBeenClicked, setHasDocButtonBeenClicked] = useState(false);
  const [shouldGlowDocButton, setShouldGlowDocButton] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const excelFileInputRef = useRef<HTMLInputElement>(null);
  const dataSourceModalRef = useRef<HTMLDialogElement>(null);
  const sessionId = useParams().sessionId || "";
  const {
    messageInputDraft,
    setMessageInputDraft,
    chatMessages,
    currentSession
  } = useGlobalStore((state: any) => state);

  // Determine which data sources to display
  const displayedDataSources = sessionId && currentSession?.data_source_ids
    ? currentSession.data_source_ids
    : selectedDataSources;

  useEffect(() => {
    if (!isHome || isShuffling || isFocused) return;

    const placeHolders = [
      "List the top 10 customers by total rentals and total payment amount",
      "Compare revenue and number of rentals between Store 1 and Store 2",
      "What are the most popular film categories by rentals?",
      "List the top 10 tracks and albums by revenue",
      "Break down revenue by route and airline partner",
      "Which airports handle the most departures and arrivals?",
    ];

    const shufflePlaceholder = () => {
      setIsShuffling(true);
      setShuffledPlaceholder("");

      const currentText = placeHolders[currentPlaceholderIndex] || "";
      let index = 0;

      const shufflePlaceholderInterval = setInterval(() => {
        if (index < currentText.length) {
          setShuffledPlaceholder(currentText.substring(0, index + 1));
          index++;
        } else {
          clearInterval(shufflePlaceholderInterval);
          setCurrentPlaceholderIndex(
            (currentPlaceholderIndex + 1) % placeHolders.length
          );
          setTimeout(() => setIsShuffling(false), 3000);
        }
      }, 50);
    };

    shufflePlaceholder();
  }, [isShuffling, isFocused]);

  // Function to identify successful AI responses
  const isSuccessfulAIResponse = (message: any) => {
    if (!message?.message) return false;
    if (message.message.sender !== "gamgee") return false;
    if (message.message.streaming === true) return false;
    
    // Check for error messages
    if (message.message.final_output?.type === "error") return false;
    
    // Check for clarification questions
    if (message.message.final_output?.type === "clarification_question") return false;
    
    // Check for successful content (rich messages, text, or asset messages with valid output)
    const messageType = message.message.message_type;
    if (messageType === "rich" || messageType === "text") return true;
    if (messageType === "asset_message" && 
        message.message.final_output && 
        message.message.final_output.type !== "error" && 
        message.message.final_output.type !== "clarification_question") {
      return true;
    }
    
    return false;
  };

  // Monitor chatMessages to determine when to show glow effect
  useEffect(() => {
    if (!chatMessages || !sessionId || hasDocButtonBeenClicked) return;
    
    const successfulMessages = chatMessages.filter(isSuccessfulAIResponse);
    const totalMessages = chatMessages.length;
    
    // Enable glow when: at least 2 successful responses OR 1 successful out of 5 messages
    const shouldGlow = successfulMessages.length >= 2 || 
                      (totalMessages >= 5 && successfulMessages.length >= 1);
    
    setShouldGlowDocButton(shouldGlow);
  }, [chatMessages, sessionId, hasDocButtonBeenClicked]);

  const { ref: scrambleRef, isVisible: isScrambleVisible } = useLoadingText(
    isLoading,
    isMessageStreaming,
    isHome
  );

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  const handleMessageChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setMessageInputDraft(e.target.value);
    adjustTextareaHeight();
  };

  const handleExcelFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      const uniqueNewFiles = newFiles.filter(
        (newFile) =>
          !attachments.some(
            (existingFile) => existingFile.name === newFile.name
          )
      );
      setAttachments((prev) => [...prev, ...uniqueNewFiles]);
    }
    if (excelFileInputRef.current) {
      excelFileInputRef.current.value = "";
    }
  };

  const handleRemoveExcelFile = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  useEffect(() => {
    setTimeout(() => adjustTextareaHeight(), 0);
  }, [messageInputDraft]);

  const isSendButtonDisabled =
    disabled ||
    !messageInputDraft ||
    !messageInputDraft.trim() ||
    (attachmentsRequired ? attachments.length === 0 : false) ||
    isLoading ||
    isMessageStreaming;

  const handleSend = () => {
    if (!isSendButtonDisabled) {
      onSendMessage(messageInputDraft, attachments);
      setMessageInputDraft("");
      setAttachments([]);
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
      }
    }
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDataSourceCardClick = () => {
    setIsDataSourceModalOpen(true);
    dataSourceModalRef.current?.showModal();
  };

  const handleDataSourceModalClose = () => {
    setIsDataSourceModalOpen(false);
    dataSourceModalRef.current?.close();
  };

  // "absolute -top-8 left-8 z-10 flex gap-2"
  return (
    <div className={`${sessionId ? "w-4xl" : "w-full"}`}>
      {/* Loading Scramble Text - Positioned above input with backdrop blur */}
      {isScrambleVisible && (
        <div
          className={`absolute ${
            attachments.length > 0 ? "bottom-40" : "bottom-28"
          } ml-3 mb z-20`}
        >
          <div className="backdrop-blur-sm bg-base-100/75 rounded-lg px-3 py-2 flex gap-2 items-center">
            <span className="loading loading-ring loading-sm text-secondary" />
            <div
              ref={scrambleRef}
              className="text-secondary text-sm font-medium"
            />
          </div>
        </div>
      )}

      <div className={`flex flex-row gap-2 justify-between px-2 min-h-12 mb-4`}>
        <div className="w-full">
          {/* File Attachments Preview */}
          {attachments.length > 0 && (
            <div className="flex items-center gap-2 p-2">
              <div className="flex flex-wrap gap-2 flex-1 max-h-[3.75rem] overflow-y-auto">
                {attachments.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-1 px-3 py-1 bg-orange-200 text-orange-800 rounded-full text-sm max-w-[18.75rem] overflow-hidden"
                  >
                    <span className="truncate">{file.name}</span>
                    <button
                      className="flex items-center justify-center hover:bg-orange-300 rounded-full p-1 transition-colors"
                      onClick={() => handleRemoveExcelFile(index)}
                      title="Remove file"
                    >
                      <FaTimes size={10} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                className="btn btn-neutral btn-ghost btn-sm btn-circle flex-shrink-0"
                onClick={() => excelFileInputRef.current?.click()}
                title="Add more files"
              >
                <FaPlus size={14} />
              </button>
            </div>
          )}

          {/* Hidden File Input */}
          <input
            ref={excelFileInputRef}
            type="file"
            className="hidden"
            onChange={handleExcelFileChange}
            accept=".xlsx, .xls, .csv"
            multiple
          />

          <div className="flex items-center gap-5 w-full bg-base-200 rounded-xl p-3 pl-5">
            <div className="flex items-center gap-2">
              {/* File Attachment Dropdown */}
              <div
                className={`dropdown ${
                  sessionId ? "dropdown-top" : "dropdown-bottom"
                }`}
              >
                <div
                  tabIndex={0}
                  role="button"
                  className={`btn btn-secondary btn-outline btn-circle btn-sm ${
                    disabled ? "btn-disabled" : ""
                  }`}
                  title="Add files"
                >
                  <FaPlus size={16} />
                </div>
                <ul
                  tabIndex={0}
                  className="dropdown-content menu bg-base-200 rounded-box w-64"
                >
                  <li>
                    <a onClick={() => excelFileInputRef.current?.click()}>
                      Add Excel or CSV Files
                    </a>
                  </li>
                </ul>
              </div>

              {/* Data Sources Button */}
              <button
                onClick={handleDataSourceCardClick}
                className={`btn btn-secondary btn-outline btn-circle btn-sm relative ${
                  disabled ? "btn-disabled" : ""
                }`}
                title="Data sources"
              >
                <TbDatabase size={18} />
                {displayedDataSources.length > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-3 w-3">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-success">
                      <FaCheck size={8} className="m-auto text-success-content" />
                    </span>
                  </span>
                )}
              </button>
            </div>

            <div className="relative flex-1 flex">
              <textarea
                ref={textareaRef}
                className="w-full bg-transparent border-none text-lg resize-none outline-none placeholder:text-base-content/50 placeholder:font-medium leading-5 p-0"
                value={messageInputDraft || ""}
                onChange={handleMessageChange}
                onKeyDown={handleKeyPress}
                onFocus={() => setIsFocused(true)}
                placeholder={
                  isHome && !isFocused ? shuffledPlaceholder : placeholder
                }
                disabled={disabled}
                rows={1}
              />
            </div>

            {/* Send Button */}
            <button
              className="btn btn-soft btn-primary btn-circle flex-shrink-0 hover:scale-95 transition-transform"
              onClick={handleSend}
              disabled={isSendButtonDisabled || isLoading}
              title="Send message"
            >
              {isLoading || isMessageStreaming ? (
                <span className="loading loading-spinner loading-sm"></span>
              ) : (
                <FaArrowUp size={16} />
              )}
            </button>
          </div>

          {/* Data Source Selector Modal */}
          <DataSourceSelectorModal
            ref={dataSourceModalRef}
            isOpen={isDataSourceModalOpen}
            onClose={handleDataSourceModalClose}
            initialSelectedSources={displayedDataSources}
            onSelectionChange={onDataSourcesChange}
          />
        </div>
        {sessionId && (
          <div className="flex items-center justify-center">
            <button
              className={`group relative btn btn-soft btn-primary overflow-hidden transition-all duration-300 ease-in-out hover:w-48 w-16 h-16 rounded-full flex items-center justify-center ${
                sessionDocumentView && "w-48 btn-active"
              } ${
                shouldGlowDocButton && !sessionDocumentView ? "animate-pulse ring-2 ring-primary" : ""
              }`}
              title="Document Mode"
              disabled={isLoading}
              onClick={() => {
                setSessionDocumentView(!sessionDocumentView);
                setHasDocButtonBeenClicked(true);
              }}
            >
              <FaFile
                size={20}
                className={`absolute transition-all duration-300 ease-in-out group-hover:left-4 left-1/2 transform -translate-x-1/2 group-hover:translate-x-0 top-1/2 -translate-y-1/2 flex-shrink-0 ${
                  sessionDocumentView && "left-4 translate-x-0"
                }`}
              />
              <span
                className={`opacity-0 group-hover:opacity-100 transition-all duration-300 ease-in-out whitespace-nowrap text-sm font-medium absolute left-12 top-1/2 transform -translate-y-1/2 ${
                  sessionDocumentView && "opacity-100"
                }`}
              >
                Document Mode
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageInput;
